import { listsV2 } from '@ecosystem/newron-design-system';

import { commonTableCellConfig } from '../../../utils/constants';
import { ColumnConfig, GroupInfo } from './types';
import { getFilteredData } from '../../utils';
/**
 * Converts grouped data map into an array of group metadata.
 * @param groupedMap - Map with group key as string and array of items.
 * @param groupKey - Optional key using which groupedMap was created. Used for sortingValue extraction.
 * @param getSortingValue - Optional function to get sorting key from an item.
 * @returns Array of group metadata
 */
export function mapToRootTableData<T extends object>(
  groupedMap: Map<string, T[]>,
  groupKey?: keyof T,
  getSortingValue?: (object: T, key: keyof T) => string | number | Date,
  searchTerm?: string,
  columnConfig?: ColumnConfig[]
): GroupInfo[] {
  if (getSortingValue && !groupKey) {
    throw new Error('groupKey is required when getSortingValue is provided.');
  }

  return Array.from(groupedMap.entries()).map(
    ([groupName, items]): GroupInfo => ({
      id: groupName,
      group: {
        label: groupName,
        count: getFilteredData(items, searchTerm, columnConfig, groupKey).length, // Count of filtered items
        // If sortingValue is provided, use it to get the sorting key, otherwise use groupName directly.
        sortingValue: getSortingValue ? getSortingValue(items?.[0], groupKey) : groupName,
      },
    })
  );
}

/**
 * Groups an array of objects by a specified key.
 * @param data - The array of objects to group.
 * @param key - The key to group by.
 * @param getGroupName - Optional function to get the group name from the value, if value is not a string or number.
 * @returns A map where the keys are the group names and the values are arrays of items in that group.
 */
export function groupByKey<T extends object, K extends keyof T>(
  data: T[],
  key: K,
  getGroupName?: (val: T[K]) => string
): Map<string, T[]> {
  const groupedMap = new Map<string, T[]>();

  for (const item of data) {
    if (!item || item[key] == null) continue;

    const rawGroupValue = item[key];
    let groupName: string;

    // If the group value is a string or number, use it directly.
    if (typeof rawGroupValue === 'string' || typeof rawGroupValue === 'number') {
      groupName = String(rawGroupValue);
    } else {
      // If it's not a string or number, use the callback to get the group name.
      if (!getGroupName) {
        throw new Error('getGroupName function is required to group the data by non-string/number keys.');
      }
      groupName = getGroupName(rawGroupValue);
    }

    // If the group is not already in the map, initialize it.
    if (!groupedMap.has(groupName)) groupedMap.set(groupName, []);

    // push the item into the group
    groupedMap.get(groupName).push(item);
  }

  return groupedMap;
}

export const prepareColumns = (columns: ColumnConfig[], groupBy: unknown): listsV2.ColumnConfig[] => {
  return columns
    .map(col => ({
      ...col,
      cellConfig: commonTableCellConfig,
      ...(col.subComponent ?? {}),
    }))
    .filter(col => col.columnKey !== (groupBy as typeof col.columnKey));
};

export const getGroupColumnConfig = (
  header: string,
  isSortingEnabled?: boolean,
  groupBy: string = 'group'
): listsV2.ColumnConfig => ({
  columnKey: groupBy,
  columnName: header,
  isSortingEnabled,
  cellConfig: {
    ...commonTableCellConfig,
    subComponentWidth: '100%',
    propagateRowClick: true,
  },
});

export const getSortingValueFromGroup = (col: GroupInfo) => col.group.sortingValue || col.group.label;

export function mapToSortingCallback<T extends object>(
  columnConfig: ColumnConfig[] | undefined,
  columnKey: keyof T
): ((row: T, key: keyof T) => string | number | Date) | undefined {
  if (!columnConfig) return undefined;

  const column = columnConfig.find(col => col.columnKey === columnKey);
  return column?.getSortingValue;
}
